/**
 * SOC lane — assessment capacity as its own strip. A slot is the SOC visit's
 * planned duration plus the assumed drive (both configuration). Supply is the
 * SOC-capable assessing disciplines, minus anyone in ramp (shown as pending).
 * **Booked** = SOC holds + scheduled SOC/ROC/recert assessments. Same numbers
 * the referral panel reads for its 48h check.
 *
 * `reservedSlots` and `flexibleSlots` are the standing SOC-block idea: minutes a
 * clinician's day has set aside for assessments. The lane **does not draw them**
 * — Pete, 2026-09-20 — because a branch that must keep blocks aside for
 * admissions is a claim this product does not make. A clinician who keeps a
 * morning open is a clinician with free time, and the search finds it like any
 * other free time. The detection stays here and in `clinicianDay` so the
 * capability is answerable in the room without a rebuild.
 */
import type { DateKey, DemoState } from "@caredna-marlin/utils/homeHealthDemo/state";
import { dateKeyRange, dateKeyStart, addDaysToKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { clinicianDayFacts } from "./clinicianDay";
import { holdMinutes } from "./capacity";
import { isInRamp, isSocCapable, rosterEntries } from "./roster";

export interface SocLaneDay {
  dateKey: DateKey;
  slotMinutes: number;
  supplyHandles: string[];
  pendingHandles: string[];
  supplyMinutes: number;
  slotsAvailable: number;
  /** Computed, never drawn — see the module comment. */
  reservedSlots: number;
  /** Computed, never drawn — see the module comment. */
  flexibleSlots: number;
  bookedHolds: number;
  bookedAssessments: number;
  booked: number;
  /** slotsAvailable − booked; negative means more booked than the day can hold. */
  open: number;
}

export function selectSocLane(state: DemoState, rangeStart: DateKey, rangeEnd: DateKey): SocLaneDay[] {
  const slotMinutes = holdMinutes(state);
  const socMinutes = state.config.socSlot.durationMinutes;
  const entries = rosterEntries(state);
  return dateKeyRange(rangeStart, rangeEnd).map((dateKey) => {
    const supplyHandles: string[] = [];
    const pendingHandles: string[] = [];
    let supplyMinutes = 0;
    let reservedSlots = 0;
    let bookedAssessments = 0;
    for (const entry of entries) {
      if (entry.roleKind !== "assessing") continue;
      const facts = clinicianDayFacts(state, entry, dateKey);
      if (!facts.hasShift) continue;
      if (isInRamp(entry, dateKey)) {
        pendingHandles.push(entry.handle);
        continue;
      }
      if (!isSocCapable(entry, dateKey)) continue;
      supplyHandles.push(entry.handle);
      supplyMinutes += Math.max(0, facts.shiftMinutes - facts.blockMinutes);
      if (facts.reservedSocBlockMinutes >= socMinutes) reservedSlots += Math.max(1, Math.floor(facts.reservedSocBlockMinutes / slotMinutes));
      bookedAssessments += facts.assessments;
    }
    const bookedHolds = Object.values(state.holds).filter((h) => h.status === "held" && h.date === dateKey).length;
    const slotsAvailable = Math.floor(supplyMinutes / slotMinutes);
    const booked = bookedHolds + bookedAssessments;
    return {
      dateKey,
      slotMinutes,
      supplyHandles,
      pendingHandles,
      supplyMinutes,
      slotsAvailable,
      reservedSlots: Math.min(reservedSlots, slotsAvailable),
      flexibleSlots: Math.max(slotsAvailable - reservedSlots, 0),
      bookedHolds,
      bookedAssessments,
      booked,
      open: slotsAvailable - booked,
    };
  });
}

/** Open SOC slots on the days that intersect [fromIso, toIso] — the referral panel's 48h check. */
export function socSlotsOpenWithin(state: DemoState, fromIso: string, toIso: string): { open: number; days: SocLaneDay[] } {
  const tz = state.config.branch.timeZone;
  const from = new Date(fromIso);
  const to = new Date(toIso);
  let cursor = dateKeyStartKey(from, tz);
  const keys: DateKey[] = [];
  while (dateKeyStart(cursor, tz) < to) {
    keys.push(cursor);
    cursor = addDaysToKey(cursor, 1);
  }
  if (keys.length === 0) return { open: 0, days: [] };
  const days = selectSocLane(state, keys[0], keys[keys.length - 1]);
  return { open: days.reduce((sum, d) => sum + Math.max(d.open, 0), 0), days };
}

function dateKeyStartKey(date: Date, tz: string): DateKey {
  // formatInTimeZone without importing the clock formatter twice
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}
